import React, { useState, useEffect } from "react";
import { Link, Redirect, useHistory } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FormattedMessage, injectIntl } from "react-intl";
import { verify2FAOtpAsync } from "../redux/authApi";
import { useDispatch, useSelector, shallowEqual } from "react-redux";

const initialValues = {
  otp: "",
};

function VerifyOtp(props) {
  const { intl } = props;
  const dispatch = useDispatch();
  const history = useHistory();

  const { user, flashMessage, isLoading, twoFATempData } = useSelector(
    (state) => state.auth,
    shallowEqual
  );

  // Formik validation schema
  const OtpSchema = Yup.object().shape({
    otp: Yup.string()
      .trim()
      .required("OTP is required")
      .matches(/^\d{6}$/, "OTP must be exactly 6 digits"),
  });

  const formik = useFormik({
    initialValues,
    validationSchema: OtpSchema,
    onSubmit: (values, { setStatus }) => {
      try {
        if (!twoFATempData) {
          setStatus(
            "Your verification session expired. Please go back to login and start again."
          );
          return;
        }
        // Call verify OTP API
        dispatch(verify2FAOtpAsync(values.otp));
      } catch (error) {
        setStatus(
          intl.formatMessage({
            id: "AUTH.VALIDATION.INVALID_OTP",
          })
        );
      }
    },
  });

  // Set classes with validations
  const getInputClasses = (fieldname) => {
    if (formik.touched[fieldname] && formik.errors[fieldname]) {
      return "is-invalid";
    }
    if (formik.touched[fieldname] && !formik.errors[fieldname]) {
      return "is-valid";
    }
    return "";
  };

  // Handle OTP input - only allow numbers and limit to 6 digits
  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    formik.setFieldValue("otp", value);
  };

  // Redirect to dashboard after successful login
  useEffect(() => {
    if (user && user._id && !twoFATempData) {
      history.push("/dashboard");
    }
  }, [user, twoFATempData, history]);

  // Reset form status when flashMessage changes (error occurred)
  useEffect(() => {
    if (flashMessage && formik.status) {
      formik.setStatus("");
    }
  }, [flashMessage]);

  // Redirect if already logged in
  if (user && user._id && !twoFATempData) {
    return <Redirect to="/dashboard" />;
  }

  const shouldRedirectToLogin = !twoFATempData && !flashMessage;

  // Redirect to login if no 2FA temp data and no error to show
  if (shouldRedirectToLogin) {
    return <Redirect to="/auth/login" />;
  }

  const verifyDisabled =
    isLoading || formik.values.otp.length !== 6 || !twoFATempData;

  return (
    <div className="login-form login-signin" id="kt_login_signin_form">
      {/* begin::Head */}
      <div className="text-center mb-10 mb-lg-20">
        <h3 className="font-size-h1 text-white">Two-Factor Authentication</h3>
        <p className="text-muted font-weight-bold">
          Enter the 6-digit code from your Google Authenticator app
        </p>
      </div>
      {/* end::Head */}
      {flashMessage ? (
        <div
          className="mb-10 alert alert-custom alert-light-danger alert-dismissible"
          style={{ margin: "auto" }}
        >
          <div className="alert-text font-weight-bold">{flashMessage}</div>
        </div>
      ) : null}
      {/*begin::Form*/}
      <form
        autoComplete="off"
        onSubmit={formik.handleSubmit}
        className="form fv-plugins-bootstrap fv-plugins-framework"
      >
        {formik.status ? (
          <div className="mb-10 alert alert-custom alert-light-danger alert-dismissible">
            <div className="alert-text font-weight-bold">{formik.status}</div>
          </div>
        ) : null}
        <div className="form-group fv-plugins-icon-container">
          <input
            placeholder="Enter 6 digit code"
            type="text"
            className={`form-control form-control-solid h-auto py-5 px-2 border-less-input text-white ${getInputClasses(
              "otp"
            )}`}
            name="otp"
            autoComplete="off"
            value={formik.values.otp}
            onChange={handleOtpChange}
            onBlur={formik.handleBlur}
            maxLength={6}
            inputMode="numeric"
          />
          {formik.touched.otp && formik.errors.otp ? (
            <div className="fv-plugins-message-container">
              <div className="fv-help-block">{formik.errors.otp}</div>
            </div>
          ) : null}
        </div>
        <div className="form-group d-flex flex-wrap justify-content-between align-items-center">
          <Link
            to="/auth/login"
            className="text-dark-50 text-hover-primary my-3 mr-2"
          >
            Back to Login
          </Link>
          <button
            id="kt_verify_otp_submit"
            type="submit"
            disabled={verifyDisabled}
            className={`btn btn-blue btn_new ${isLoading ? "lodingTime" : ""}`}
          >
            {isLoading ? (
              <>
                <span className="spinner spinner-white mr-2"></span>
                <span>Verifying...</span>
              </>
            ) : (
              <span>Verify OTP</span>
            )}
          </button>
        </div>
      </form>
      {/*end::Form*/}
    </div>
  );
}

export default injectIntl(VerifyOtp);

